/**
 * @fileoverview Genre taxonomy constants.
 *
 * Pure data definitions for the genre hierarchy. This is the canonical
 * source of truth for genre categories and their templates.
 */

import type { GenreTemplate } from '@/domains/style-guides/schemas';

/**
 * Defines the hierarchical structure of genre style guides.
 */
export interface StyleGuideHierarchy {
    [category: string]: {
      description: string;
      templates: GenreTemplate[];
    };
  }

export const styleGuideHierarchy: StyleGuideHierarchy = {
    "Adventure": {
        description: "Fast-paced styles centered on a journey, a quest, or a high-stakes undertaking, often in exotic or dangerous locations.",
        templates: [
            {
                templateId: 'adventure-general',
                name: 'General Adventure',
                description: 'A general-purpose adventure style focusing on journey and discovery.',
            },
            {
                templateId: 'adventure-quest',
                name: 'Quest Adventure',
                description: 'The plot revolves around a journey to a specific destination to achieve a goal, such as finding an object or rescuing a person.',
            },
            {
                templateId: 'adventure-survival',
                name: 'Survival Adventure',
                description: 'Focuses on the protagonist\'s struggle to survive against the elements, often in a wilderness or disaster scenario.',
            },
        ],
    },
    "Childrens Fiction": {
        description: "Styles tailored for young audiences, from early readers to more complex middle-grade novels.",
        templates: [
            {
                templateId: 'childrens-fiction-general',
                name: 'General Children\'s Fiction',
                description: 'A versatile style for stories aimed at young readers, focusing on clear narrative and relatable themes.',
            },
            {
                templateId: 'childrens-early-readers',
                name: 'Early Readers (5-7)',
                description: 'For the newest independent readers. Simple vocabulary, clear sentence structures, and a focus on visual storytelling support.',
            },
            {
                templateId: 'childrens-chapter-books',
                name: 'Chapter Books (6–9)',
                description: 'For children transitioning to longer narratives. Features developing plots, recurring characters, and divided chapters.',
            },
            {
                templateId: 'childrens-middle-grade',
                name: 'Middle Grade (9–12)',
                description: 'For confident readers exploring complex themes. Features intricate plots and deep character development.',
            },
        ]
    },
    "Erotica": {
        description: "Styles that place the sexual relationship at the core of the narrative, exploring physical and emotional intimacy in explicit detail.",
        templates: [
            {
                templateId: 'erotica-general',
                name: 'General Erotica',
                description: 'A general-purpose erotica style focusing on intimacy and desire.',
            },
            {
                templateId: 'erotica-romance',
                name: 'Erotic Romance',
                description: 'Prioritizes the sexual aspects of the relationship, with explicit content that is integral to the plot and character development.',
            },
            {
                templateId: 'erotica-bdsm',
                name: 'BDSM Erotica',
                description: 'Focuses on consensual power dynamics, trust, and the physical and psychological aspects of BDSM relationships.',
            },
            {
                templateId: 'erotica-lgbtq',
                name: 'LGBTQ+ Erotica',
                description: 'Celebrates queer love and desire, exploring a diverse range of identities and relationships with authenticity.',
            },
            {
                templateId: 'erotica-paranormal',
                name: 'Paranormal Erotica',
                description: 'Blends supernatural or fantastical elements with explicit intimacy, exploring relationships with non-human beings.',
            },
            {
                templateId: 'erotica-pornography',
                name: 'Pornography',
                description: 'Explicit sexual content as the primary focus, with graphic depictions of physical acts and minimal plot.',
            },
        ]
    },
    "Fantasy": {
        description: "Styles focused on creating a sense of wonder, often involving unique magic systems, epic conflicts, and intricate world-building.",
        templates: [
             {
                templateId: 'fantasy-general',
                name: 'General Fantasy',
                description: 'A broad fantasy style suitable for a wide range of magical settings and stories.',
            },
             {
                templateId: 'fantasy-high-epic',
                name: 'High / Epic Fantasy',
                description: 'Focuses on large-scale conflicts, detailed world-building, and often a battle between good and evil.',
            },
            {
                templateId: 'fantasy-low',
                name: 'Low Fantasy',
                description: 'Set in a realistic, recognizable world where magic is rare, mysterious, or subtle.',
            },
            {
                templateId: 'fantasy-dark',
                name: 'Dark Fantasy',
                description: 'Blends fantasy elements with horror, featuring morally gray characters and unsettling themes.',
            },
            {
                templateId: 'fantasy-urban',
                name: 'Urban Fantasy',
                description: 'Magic and mythological creatures exist hidden within a modern, urban setting.',
            },
            {
                templateId: 'fantasy-portal',
                name: 'Portal Fantasy',
                description: 'A story where the protagonist is transported from their world to a magical one.',
            },
            {
                templateId: 'fantasy-sword-and-sorcery',
                name: 'Sword and Sorcery',
                description: 'Focuses on personal heroism, thrilling action, and daring protagonists in a dangerous world.',
            },
        ]
    },
    "Historical": {
        description: "Styles that immerse the reader in a specific time period, focusing on authenticity in setting, social norms, and character voice.",
        templates: [
             {
                templateId: 'historical-general',
                name: 'General Historical',
                description: 'A general style for stories set in the past, focusing on period authenticity.',
            },
            {
                templateId: 'historical-fiction',
                name: 'Historical Fiction',
                description: 'A narrative set in the past, blending fictional characters with real historical events, figures, or settings to tell a compelling story.',
            },
            {
                templateId: 'historical-mystery',
                name: 'Historical Mystery',
                description: 'A mystery set in a well-researched past era, where the historical context is integral to the plot.',
            },
        ],
    },
    "Horror": {
        description: "Styles designed to provoke fear, dread, and terror in the reader, ranging from supernatural to psychological horror.",
        templates: [
            {
                templateId: 'horror-general',
                name: 'General Horror',
                description: 'A versatile horror style suitable for creating suspense, dread, and terror.',
            },
            {
                templateId: 'horror-psychological',
                name: 'Psychological Horror',
                description: 'Relies on characters\' internal fears, guilt, and emotional instability to create horror, often blurring the line between the real and the imagined.',
            },
            {
                templateId: 'horror-supernatural',
                name: 'Supernatural Horror',
                description: 'Features ghosts, demons, malevolent spirits, and other paranormal phenomena as the source of fear.',
            },
            {
                templateId: 'horror-gothic',
                name: 'Gothic Horror',
                description: 'Combines horror with romance and melodrama, set in atmospheric, often decaying, locations like old castles or isolated manors.',
            },
            {
                templateId: 'horror-body',
                name: 'Body Horror',
                description: 'Derives its horror from the graphic destruction, degeneration, or grotesque transformation of the human body.',
            },
        ],
    },
    "Literary Fiction": {
        description: "Styles that focus on the craft of writing itself, known for rich, poetic prose, deep character exploration, and thematic complexity.",
        templates: [
            {
                templateId: 'literary-fiction-general',
                name: 'General Literary Fiction',
                description: 'Focuses on character depth, thematic complexity, and the quality of the prose over plot mechanics.',
            }
        ]
    },
    "Mystery": {
        description: "Direct, plot-driven styles that build suspense by focusing on a central puzzle or crime that must be solved.",
        templates: [
             {
                templateId: 'mystery-general',
                name: 'General Mystery',
                description: 'A foundational style for crafting puzzles, building suspense, and following clues to a logical conclusion.',
            },
            {
                templateId: 'mystery-cozy',
                name: 'Cozy Mystery',
                description: 'Features an amateur detective, a small-town setting, and a distinct lack of graphic violence or gore.',
            },
            {
                templateId: 'mystery-hardboiled',
                name: 'Hardboiled Detective',
                description: 'A tough, cynical private investigator navigates a corrupt world to uncover the truth, often with a noir tone.',
            },
            {
                templateId: 'mystery-police-procedural',
                name: 'Police Procedural',
                description: 'Focuses on the realistic, step-by-step investigation of a crime by a team of police officers.',
            },
            {
                templateId: 'mystery-locked-room',
                name: 'Locked-Room Mystery',
                description: 'A crime—usually a murder—is committed under seemingly impossible circumstances.',
            },
            {
                templateId: 'mystery-amateur-sleuth',
                name: 'Amateur Sleuth',
                description: 'An ordinary person with a specific skill set or profession is drawn into solving a crime.',
            },
        ]
    },
    "Romance": {
        description: "Character-centric styles focused on the development of a romantic relationship, emphasizing emotional connection and a satisfying conclusion.",
        templates: [
             {
                templateId: 'romance-general',
                name: 'General Romance',
                description: 'A flexible style focusing on the central romantic journey, emotional connection, and a satisfying, hopeful ending.',
            },
            {
                templateId: 'romance-contemporary',
                name: 'Contemporary Romance',
                description: 'Focuses on modern-day relationships, exploring emotional intimacy and realistic challenges.',
            },
            {
                templateId: 'romance-historical',
                name: 'Historical Romance',
                description: 'Love stories set in the past, where societal rules and historical context shape the characters\' journey.',
            },
            {
                templateId: 'romance-paranormal',
                name: 'Paranormal Romance',
                description: 'A romance that involves supernatural beings like vampires, werewolves, or ghosts.',
            },
            {
                templateId: 'romance-suspense',
                name: 'Romantic Suspense',
                description: 'Combines the elements of a romance with a thriller or mystery plot, creating high stakes for the couple.',
            },
            {
                templateId: 'romance-comedy',
                name: 'Romantic Comedy',
                description: 'A lighthearted and humorous take on romance, often featuring witty banter and amusing situations.',
            }
        ]
    },
    "Science Fiction": {
        description: "Styles that balance detailed, immersive world-building with dynamic storytelling to transport readers to other worlds.",
        templates: [
            {
                templateId: 'science-fiction-general',
                name: 'General Sci-Fi',
                description: 'A versatile sci-fi style for stories focused on technology, the future, and speculative concepts.',
            },
            {
                templateId: 'sci-fi-hard',
                name: 'Hard Sci-Fi',
                description: 'Prioritizes scientific accuracy and technical detail. Explores the logical consequences of technological advancement.',
            },
            {
                templateId: 'sci-fi-space-opera',
                name: 'Space Opera',
                description: 'Focuses on epic scales, galactic empires, and dramatic character-driven stories against a backdrop of interstellar war and adventure.',
            },
            {
                templateId: 'sci-fi-cyberpunk',
                name: 'Cyberpunk',
                description: '"High tech, low life." Explores the impact of technology on society, often in gritty, dystopian urban settings.',
            },
            {
                templateId: 'sci-fi-post-apocalyptic',
                name: 'Post-Apocalyptic',
                description: 'Set in a world after a catastrophic event. Focuses on survival, rebuilding, and the human condition in a desolate landscape.',
            },
            {
                templateId: 'sci-fi-military',
                name: 'Military Sci-Fi',
                description: 'Centers on interstellar conflict, military tactics, and the lives of soldiers in futuristic warfare scenarios.',
            },
             {
                templateId: 'sci-fi-first-contact',
                name: 'First Contact',
                description: 'Focuses on the initial encounter between humanity and an extraterrestrial intelligence, exploring themes of communication, xenophobia, and discovery.',
            },
             {
                templateId: 'sci-fi-biopunk',
                name: 'Biopunk',
                description: 'A subgenre of cyberpunk focusing on the implications of biotechnology, genetic engineering, and bio-hacking.',
            },
        ]
    },
    "Thriller": {
        description: "Fast-paced styles that prioritize tension, surprise, and high stakes to keep the reader on the edge of their seat.",
        templates: [
             {
                templateId: 'thriller-general',
                name: 'General Thriller',
                description: 'A foundational style for building suspense, creating high stakes, and delivering a fast-paced, plot-driven narrative.',
            },
            {
                templateId: 'thriller-psychological',
                name: 'Psychological Thriller',
                description: 'Focuses on the unstable mental and emotional states of its characters, creating suspense through unreliable narration and mind games.',
            },
            {
                templateId: 'thriller-spy',
                name: 'Spy Thriller',
                description: 'Centers on espionage, government conspiracies, and the high-stakes world of intelligence agencies.',
            },
            {
                templateId: 'thriller-action',
                name: 'Action Thriller',
                description: 'Characterized by high-octane action sequences, relentless pacing, and a protagonist facing overwhelming physical threats.',
            },
        ],
    },
    "Young Adult": {
        description: "Sophisticated styles for teen readers that explore mature themes with a strong focus on character voice and emotional depth.",
        templates: [
            {
                templateId: 'young-adult-general',
                name: 'General Young Adult',
                description: 'A broad style for teen fiction that balances character voice with plot, suitable for many subgenres.',
            },
            {
                templateId: 'young-adult-contemporary',
                name: 'YA Contemporary',
                description: 'Focuses on realistic characters and issues in a modern setting. Voice and emotional honesty are paramount.',
            },
            {
                templateId: 'young-adult-speculative',
                name: 'YA Speculative',
                description: 'Covers Sci-Fi and Fantasy sub-genres. Blends high-concept worlds with the intense emotional journey of its teen protagonist.',
            },
            {
                templateId: 'young-adult-thriller',
                name: 'YA Thriller',
                description: 'High-stakes, fast-paced plots where teen protagonists face dangerous situations. Focuses on suspense and tension.',
            },
        ]
    },
};
